/**
 * Canonical scope-param helpers ("typeId:recordId,typeId:recordId,…") — the
 * single source of truth for Library scope parsing.
 *
 * A ref's record segment may be the wildcard `*` — a Category (type-wide)
 * scope, `typeId:*`, matching everything about that type. It round-trips
 * through parse/encode unchanged; consumers branch on `isCategoryScopeItem`.
 */

/**
 * One scope ref. `recordId === '*'` is a Category (type-wide) scope —
 * "everything about this type", encoded as `typeId:*` in the URL and the
 * backend scope grammar.
 */
export type ScopeItem = {typeId: string; recordId: string};

export const CATEGORY_SCOPE_RECORD_ID = '*';

export function isCategoryScopeItem(item: ScopeItem): boolean {
  return item.recordId === CATEGORY_SCOPE_RECORD_ID;
}

export function parseScopeParam(param: string): ScopeItem[] {
  return param
    .split(',')
    .map((part) => {
      const colonIdx = part.indexOf(':');
      if (colonIdx === -1) return null;
      return {
        typeId: part.slice(0, colonIdx),
        recordId: part.slice(colonIdx + 1),
      };
    })
    .filter(
      (x): x is ScopeItem =>
        x !== null && x.typeId.length > 0 && x.recordId.length > 0
    );
}

export function encodeScopeParam(items: ScopeItem[]): string {
  return items.map((item) => `${item.typeId}:${item.recordId}`).join(',');
}

/** Order-insensitive identity for a scope — used to match a URL scope against
 *  a saved Page (rail active-page highlighting, header pin/unpin state). */
export function scopeKeyOf(items: Array<{typeId: string; recordId: string}>) {
  return items
    .map((item) => `${item.typeId}:${item.recordId}`)
    .sort()
    .join(',');
}

/** Whether a record is already present in the scope parameter. */
export function isRecordInScope(
  scopeStr: string,
  typeId: string,
  recordId: string
): boolean {
  const key = `${typeId}:${recordId}`;
  return scopeStr.split(',').includes(key);
}

/**
 * Scope items as upload/association payloads. A Category's `'*'` is a
 * URL/query token, not a record id — on the write side it means a type-only
 * association, which the API spells `object_record_id: null`. Sending the
 * literal `'*'` fails Pydantic's `Optional[UUID]` with a 422.
 */
export function toAssociationPayload(
  scopeItems: ScopeItem[]
): Array<{objectTypeId: string; objectRecordId: string | null}> {
  return scopeItems.map((item) => ({
    objectTypeId: item.typeId,
    objectRecordId: isCategoryScopeItem(item) ? null : item.recordId,
  }));
}
